import React, { useState } from 'react';
import { Button, Result, Segmented, Skeleton, Tooltip } from 'antd';
import { PlusOutlined, CameraOutlined } from '@ant-design/icons';
import { useIsModuleEnabled } from '../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../constants/moduleKeys';
import { useAppSelector } from '../hooks/redux';
import {
  canManageFinanzas,
  canRegisterExpenses,
  canSeeAllExpenses,
  isSupportUser,
} from '../utils/roleHelpers';
import { useResponsive } from '../hooks/useResponsive';
import ExpensesTab from '../components/finanzas/ExpensesTab';
import PnLTab from '../components/finanzas/PnLTab';
import '../components/finanzas/finanzas.css';

type TabKey = 'expenses' | 'pnl';

const Finanzas: React.FC = () => {
  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState<TabKey>('expenses');
  // Señales para que ExpensesTab abra sus modales desde la barra. Un contador y no un
  // booleano: dos clics seguidos en el mismo botón tienen que volver a abrirlo.
  const [addExpenseSignal, setAddExpenseSignal] = useState(0);
  const [captureSignal, setCaptureSignal] = useState(0);

  // Route guards mirroring the sidebar gate. Without these, an authenticated user
  // could land here by URL on a tenant where FinanzasActiva is off — sidebar
  // hides the entry but the route was loading anyway. Backend also rejects the
  // API calls (server-side enforcement in ExpensesController/FinanceController),
  // so this is defense in depth + cleaner UX than a screen full of 403 toasts.
  //
  // Tres alcances, no dos:
  //   · canManage      → todo, incluido el Estado de Resultados.
  //   · Gerente        → captura y ve todos los gastos, sin el P&L.
  //   · alcance propio → captura y ve LOS SUYOS. Ni tabs, ni KPIs, ni export.
  // Ver docs/finanzas-module.md, "Gerente y caja".
  const { user } = useAppSelector((state) => state.auth);
  const isSupport = isSupportUser(user?.email);
  const canManage = canManageFinanzas(user?.role, isSupport);
  const canEnter = canRegisterExpenses(user?.role, isSupport);
  const seesEverything = canSeeAllExpenses(user?.role, isSupport);

  // Module enabled = setting on OR Profesional plan bypass. Mirrors the backend
  // [RequireModule(ModuleKeys.Finanzas)] gate; backend stays the real authority.
  const { enabled: isFinanzasEnabled, isLoading: isLoadingSetting } = useIsModuleEnabled(MODULE_KEYS.Finanzas);

  if (isLoadingSetting) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (!canEnter) {
    return (
      <Result
        status="403"
        title="Acceso restringido"
        subTitle="Tu usuario no tiene permiso para registrar gastos."
      />
    );
  }

  if (!isFinanzasEnabled) {
    return (
      <Result
        status="403"
        title="Módulo de Finanzas no habilitado"
        subTitle="Este restaurante no tiene activado el módulo. Contacta a soporte si quieres habilitarlo."
      />
    );
  }

  // Para quien sólo ve lo suyo, "Finanzas" promete un módulo que no va a encontrar.
  // La pantalla que recibe es, literalmente, sus gastos.
  const pageTitle = seesEverything ? 'Finanzas' : 'Mis gastos';

  /**
   * Las dos altas viven juntas en la barra. Antes la captura por foto estaba suelta
   * arriba de la tabla, debajo de los KPIs: el camino más largo hasta la acción
   * principal justo en el dispositivo donde más se usa.
   *
   * En móvil quedan sólo los iconos — el texto no cabe junto al título sin empujar la
   * barra a dos renglones, y con dos botones de la misma familia el icono alcanza.
   */
  const actions = (
    <div className="fin-bar__actions">
      <Tooltip title={isMobile ? 'Capturar de un comprobante' : undefined}>
        <Button
          icon={<CameraOutlined />}
          onClick={() => setCaptureSignal((s) => s + 1)}
          aria-label="Capturar de un comprobante"
        >
          {!isMobile && 'Capturar ticket'}
        </Button>
      </Tooltip>
      <Tooltip title={isMobile ? 'Nuevo gasto' : undefined}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddExpenseSignal((s) => s + 1)}
          aria-label="Nuevo gasto"
        >
          {!isMobile && 'Nuevo gasto'}
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <div>
      <div className="fin-bar">
        <h2 className="fin-bar__title">{pageTitle}</h2>
        {/* Punto 6 de 7 del barrido: con un solo tab visible, la barra de tabs sobra. */}
        {canManage && (
          <Segmented
            value={activeTab}
            onChange={(value) => setActiveTab(value as TabKey)}
            options={[
              { label: 'Gastos', value: 'expenses' },
              { label: isMobile ? 'Resultados' : 'Estado de Resultados', value: 'pnl' },
            ]}
          />
        )}
        <div className="fin-bar__spacer" />
        {activeTab === 'expenses' && actions}
      </div>

      {/* El `canManage &&` en el tab de P&L no es redundante con ocultar el Segmented:
          protege contra un activeTab que quede en 'pnl' por cualquier otro camino. */}
      {activeTab === 'expenses' && (
        <ExpensesTab addSignal={addExpenseSignal} captureSignal={captureSignal} />
      )}
      {activeTab === 'pnl' && canManage && <PnLTab />}
    </div>
  );
};

export default Finanzas;
